import ExcelJS from "exceljs";

export interface BulkUploadValidationError {
  row: number;
  field: string;
  message: string;
}

export interface BulkUploadValidationResult {
  isValid: boolean;
  errors: BulkUploadValidationError[];
  dataRowCount: number;
}

export const styleAndProtectDummyRow = async (
  worksheet: ExcelJS.Worksheet,
  dummyRow?: ExcelJS.Row
): Promise<void> => {
  if (dummyRow) {
    dummyRow.eachCell({ includeEmpty: true }, (cell: any) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F2F2F2" }, // light grey background
      };
      cell.font = {
        name: "Arial",
        size: 10,
        italic: true,
        color: { argb: "666666" },
      };
    });
  }

  // Format date columns as Text ('@') so Excel won't convert typed YYYY-MM-DD dates into serial numbers
  const headerRow = worksheet.getRow(3);
  if (headerRow) {
    headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      const headerText = String(cell.value ?? "").toLowerCase();
      if (headerText.includes("date") || headerText.includes("time")) {
        const col = worksheet.getColumn(colNumber);
        if (col) {
          col.numFmt = "@";
        }
      }
    });
  }
};

export const cleanUploadedExcelFile = async (
  file: File,
  templateInput?: Record<string, any> | Record<string, any>[]
): Promise<File> => {
  if (!file || !templateInput) {
    return file;
  }

  const templates = Array.isArray(templateInput) ? templateInput : [templateInput];

  const matchesDummy = (rowVals: string[]): boolean => {
    return templates.some((templateObj) => {
      const dummySku = String(templateObj?.sku || templateObj?.SKU || "").trim().toLowerCase();
      const dummyItemName = String(templateObj?.itemName || templateObj?.item_name || "").trim().toLowerCase();

      const rowItemName = String(rowVals[0] ?? "").trim().toLowerCase();
      const rowSku = String(rowVals[1] ?? "").trim().toLowerCase();

      // Only consider as dummy placeholder row if SKU or ItemName is unchanged from sample template placeholders
      const skuIsUnchanged = Boolean(dummySku && rowSku && rowSku === dummySku);
      const itemNameIsUnchanged = Boolean(dummyItemName && rowItemName && rowItemName === dummyItemName);

      // Also check for staff template dummy row ("John Doe" / "john.doe@gwarm.net")
      const dummyFullName = String(
        templateObj?.["Full Name (English)"] ||
        templateObj?.["full_name_(english)"] ||
        templateObj?.fullName ||
        templateObj?.name ||
        ""
      ).trim().toLowerCase();
      const dummyEmail = String(
        templateObj?.["Work Email"] ||
        templateObj?.["work_email"] ||
        templateObj?.email ||
        ""
      ).trim().toLowerCase();

      const staffIsDummy = Boolean(
        dummyFullName &&
        rowVals.some((v) => v === dummyFullName) &&
        (!dummyEmail || rowVals.some((v) => v === dummyEmail))
      );

      return skuIsUnchanged || itemNameIsUnchanged || staffIsDummy;
    });
  };

  // Handle CSV files
  if (file.name.match(/\.csv$/i)) {
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/);
      if (lines.length < 4) return file;

      // Only check line 4 (index 3) if it matches dummy sample data
      const colsLine4 = (lines[3] || "")
        .split(",")
        .map((c) => c.replace(/^"|"$/g, "").trim().toLowerCase());

      if (matchesDummy(colsLine4)) {
        const cleanedLines = lines.filter((_, idx) => idx !== 3);
        return new File([cleanedLines.join("\n")], file.name, { type: file.type || "text/csv" });
      }
      return file;
    } catch (e) {
      console.error("Error cleaning CSV upload file:", e);
      return file;
    }
  }

  // Handle Excel (XLSX / XLS) files
  if (file.name.match(/\.(xlsx|xls)$/i)) {
    try {
      const workbook = new ExcelJS.Workbook();
      const arrayBuffer = await file.arrayBuffer();
      await workbook.xlsx.load(arrayBuffer);

      const worksheet = workbook.worksheets[0];
      if (!worksheet) return file;

      let modified = false;

      // Only check Row 4 (the designated template sample placeholder row)
      if (worksheet.rowCount >= 4) {
        const row4 = worksheet.getRow(4);
        const row4Vals: string[] = [];
        row4.eachCell({ includeEmpty: true }, (cell) => {
          row4Vals.push(String(cell.value ?? "").trim().toLowerCase());
        });

        if (matchesDummy(row4Vals)) {
          worksheet.spliceRows(4, 1);
          modified = true;
        }
      }

      // Map column headings from Row 3 to detect date columns
      const headers: Record<number, string> = {};
      const headerRow = worksheet.getRow(3);
      if (headerRow) {
        headerRow.eachCell({ includeEmpty: true }, (cell, colIdx) => {
          headers[colIdx] = String(cell.value ?? "").toLowerCase();
        });
      }

      // Normalize date cells across all remaining data rows (Row 4 onwards)
      for (let r = 4; r <= worksheet.rowCount; r++) {
        const row = worksheet.getRow(r);
        row.eachCell({ includeEmpty: true }, (cell, colIdx) => {
          const colName = headers[colIdx] || "";
          const isDateCol = colName.includes("date") || colName.includes("time") || colName.includes("dob");

          // Case A: Cell is a JS Date object
          if (cell.value instanceof Date || cell.type === ExcelJS.ValueType.Date) {
            const val = cell.value instanceof Date ? cell.value : new Date(String(cell.value));
            if (!isNaN(val.getTime())) {
              const safeDate = new Date(val.getTime() + 12 * 60 * 60 * 1000); // add 12 hours to prevent timezone shift across midnight
              const year = safeDate.getUTCFullYear();
              const month = String(safeDate.getUTCMonth() + 1).padStart(2, "0");
              const day = String(safeDate.getUTCDate()).padStart(2, "0");
              cell.value = `${year}-${month}-${day}`;
              cell.numFmt = "@";
              modified = true;
            }
          }
          // Case B: Stored as a number (Excel serial date number, e.g. 46218 for 2026-07-15)
          else if (typeof cell.value === "number" && cell.value > 30000 && cell.value < 100000 && isDateCol) {
            const excelEpoch = new Date(Date.UTC(1899, 11, 30));
            const safeDate = new Date(excelEpoch.getTime() + cell.value * 86400000 + 12 * 3600000);
            if (!isNaN(safeDate.getTime())) {
              const year = safeDate.getUTCFullYear();
              const month = String(safeDate.getUTCMonth() + 1).padStart(2, "0");
              const day = String(safeDate.getUTCDate()).padStart(2, "0");
              cell.value = `${year}-${month}-${day}`;
              cell.numFmt = "@";
              modified = true;
            }
          }
          // Case C: Formatted string with slashes or dots (DD/MM/YYYY or MM/DD/YYYY)
          else if (typeof cell.value === "string" && isDateCol) {
            const strVal = cell.value.trim();
            const slashMatch = strVal.match(/^(\d{1,2})[\/\.-](\d{1,2})[\/\.-](\d{4})$/);
            if (slashMatch) {
              const p1 = parseInt(slashMatch[1], 10);
              const p2 = parseInt(slashMatch[2], 10);
              const year = slashMatch[3];
              let day = p1;
              let month = p2;
              if (p1 > 12) {
                day = p1;
                month = p2;
              } else if (p2 > 12) {
                month = p1;
                day = p2;
              }
              const formattedMonth = String(month).padStart(2, "0");
              const formattedDay = String(day).padStart(2, "0");
              cell.value = `${year}-${formattedMonth}-${formattedDay}`;
              cell.numFmt = "@";
              modified = true;
            }
          }
        });
      }

      if (!modified) {
        return file;
      }

      const buffer = await workbook.xlsx.writeBuffer();
      return new File([buffer], file.name, { type: file.type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    } catch (err) {
      console.error("Error cleaning upload file:", err);
      return file;
    }
  }

  return file;
};

/**
 * Validates Order Bulk Upload File (LM or FM) on Client Side before uploading to backend.
 * Checks for missing required fields, invalid phone formats, duplicate reference numbers,
 * missing SKU/quantity in FM orders, COD vs CC rules, and multi-item consistency.
 */
export const validateOrderBulkUploadFile = async (
  file: File,
  orderType: "LM" | "FM"
): Promise<BulkUploadValidationResult> => {
  const errors: BulkUploadValidationError[] = [];
  let dataRowsParsed: Array<{ rowNumber: number; data: Record<string, string> }> = [];

  if (!file) {
    return {
      isValid: false,
      errors: [{ row: 0, field: "file", message: "No file selected." }],
      dataRowCount: 0,
    };
  }

  try {
    if (file.name.match(/\.csv$/i)) {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);

      if (lines.length === 0) {
        return {
          isValid: false,
          errors: [{ row: 0, field: "file", message: "File is completely empty." }],
          dataRowCount: 0,
        };
      }

      let headerIdx = 0;
      let startDataIdx = 1;

      // Check if standard template format (4 rows header layout) or raw CSV
      if (lines.length >= 3 && lines[2].toLowerCase().includes("client_id")) {
        headerIdx = 2; // Row 3 (0-indexed 2)
        startDataIdx = 3; // Row 4+ (or skip dummy if matching)
      } else if (lines[0].toLowerCase().includes("client_id") || lines[0].toLowerCase().includes("reference")) {
        headerIdx = 0;
        startDataIdx = 1;
      }

      const headers = lines[headerIdx]
        .split(",")
        .map((h) => h.replace(/^"|"$/g, "").trim().toLowerCase());

      for (let i = startDataIdx; i < lines.length; i++) {
        const rowVals = lines[i]
          .split(",")
          .map((v) => v.replace(/^"|"$/g, "").trim());

        // Skip empty lines or dummy placeholder row
        if (rowVals.every((v) => !v)) continue;
        if (i === 3 && (rowVals.includes("CL-1001") || rowVals.includes("REF-FM-1001") || rowVals.includes("PROD-5001"))) {
          continue; // Skip dummy template row
        }

        const rowObj: Record<string, string> = {};
        headers.forEach((h, colIdx) => {
          if (h) rowObj[h] = rowVals[colIdx] ?? "";
        });

        dataRowsParsed.push({ rowNumber: i + 1, data: rowObj });
      }
    } else if (file.name.match(/\.(xlsx|xls)$/i)) {
      const workbook = new ExcelJS.Workbook();
      const arrayBuffer = await file.arrayBuffer();
      await workbook.xlsx.load(arrayBuffer);

      const worksheet = workbook.worksheets[0];
      if (!worksheet || worksheet.rowCount === 0) {
        return {
          isValid: false,
          errors: [{ row: 0, field: "file", message: "Excel sheet is empty." }],
          dataRowCount: 0,
        };
      }

      let headerRowIndex = 3;
      let headerRow = worksheet.getRow(headerRowIndex);
      let headers: Record<number, string> = {};

      headerRow.eachCell({ includeEmpty: true }, (cell, colIdx) => {
        headers[colIdx] = String(cell.value ?? "").trim().toLowerCase();
      });

      // Fallback to Row 1 if Row 3 has no client_id
      if (!Object.values(headers).some((h) => h.includes("client_id") || h.includes("reference"))) {
        headerRowIndex = 1;
        headerRow = worksheet.getRow(1);
        headers = {};
        headerRow.eachCell({ includeEmpty: true }, (cell, colIdx) => {
          headers[colIdx] = String(cell.value ?? "").trim().toLowerCase();
        });
      }

      for (let r = headerRowIndex + 1; r <= worksheet.rowCount; r++) {
        const row = worksheet.getRow(r);
        const rowObj: Record<string, string> = {};
        let hasValue = false;

        row.eachCell({ includeEmpty: true }, (cell, colIdx) => {
          const val = String(cell.value ?? "").trim();
          if (val) hasValue = true;
          const colHeader = headers[colIdx];
          if (colHeader) rowObj[colHeader] = val;
        });

        if (!hasValue) continue;

        // Skip sample dummy placeholder row (usually Row 4)
        if (
          r === 4 &&
          (rowObj["client_id"] === "CL-1001" ||
            rowObj["reference_no"] === "REF-FM-1001" ||
            rowObj["product_id"] === "PROD-5001" ||
            rowObj["receiver_name"] === "Ahmed Al-Ghamdi")
        ) {
          continue;
        }

        dataRowsParsed.push({ rowNumber: r, data: rowObj });
      }
    } else {
      return {
        isValid: false,
        errors: [{ row: 0, field: "file", message: "Unsupported file format. Please upload .xlsx, .xls, or .csv" }],
        dataRowCount: 0,
      };
    }
  } catch (err: any) {
    console.error("Error reading file for validation:", err);
    return {
      isValid: false,
      errors: [{ row: 0, field: "file", message: `Failed to read file: ${err.message || err}` }],
      dataRowCount: 0,
    };
  }

  if (dataRowsParsed.length === 0) {
    return {
      isValid: false,
      errors: [{ row: 0, field: "file", message: "Sheet contains no valid data rows to upload." }],
      dataRowCount: 0,
    };
  }

  // --- Trackers for Duplicate & Consistency Checks ---
  const seenLmRefNumbers: Map<string, number> = new Map(); // ref_no -> first row
  const fmOrderGroups: Map<
    string,
    {
      firstRow: number;
      receiverName: string;
      receiverMobile: string;
      paymentType: string;
      codAmount: string;
      seenSkus: Map<string, number>; // sku -> row
    }
  > = new Map();

  // --- Row-by-Row Field Validation ---
  dataRowsParsed.forEach(({ rowNumber, data }) => {
    const clientId = (data["client_id"] || data["client id"] || "").trim();
    const refNo = (data["reference_no"] || data["reference no"] || data["reference"] || "").trim();
    const receiverName = (data["receiver_name"] || data["receiver name"] || data["full_name"] || "").trim();
    const receiverMobile = (data["receiver_mobile"] || data["receiver mobile"] || data["mobile"] || "").trim();
    const receiverCountry = (data["receiver_country"] || data["receiver country"] || data["country"] || "").trim();
    const receiverCity = (data["receiver_city"] || data["receiver city"] || data["city"] || "").trim();
    const receiverAddress = (data["receiver_address"] || data["receiver address"] || data["address"] || "").trim();
    const nationalAddress = (
      data["receiver_national_address"] ||
      data["national_address"] ||
      data["national address"] ||
      ""
    ).trim();
    const paymentType = (data["payment_type"] || data["payment type"] || data["payment_method"] || "").toUpperCase().trim();
    const codAmountStr = (data["cod_amount"] || data["cod amount"] || data["cod"] || "0").trim();
    const piecesStr = (data["no_of_pieces"] || data["no of pieces"] || data["pieces"] || "1").trim();
    const weightStr = (data["weight"] || "0").trim();
    const productId = (data["product_id"] || data["product id"] || data["sku"] || "").trim();
    const quantityStr = (data["quantity"] || data["qty"] || "").trim();

    // 1. Mandatory Core Fields
    if (!clientId) {
      errors.push({ row: rowNumber, field: "client_id", message: "Client ID is required." });
    }

    if (!refNo) {
      errors.push({ row: rowNumber, field: "reference_no", message: "Reference No is required." });
    }

    if (!receiverName) {
      errors.push({ row: rowNumber, field: "receiver_name", message: "Receiver Name is required." });
    }

    // 2. Mobile Number Validation
    if (!receiverMobile) {
      errors.push({ row: rowNumber, field: "receiver_mobile", message: "Receiver Mobile is required." });
    } else {
      const cleanMobile = receiverMobile.replace(/^(\+966|966|00966)/, "").replace(/[\s\-\+\(\)]/g, "");
      if (!/^\d{8,12}$/.test(cleanMobile)) {
        errors.push({
          row: rowNumber,
          field: "receiver_mobile",
          message: `Invalid Mobile number '${receiverMobile}'. Must be 9 to 12 digits.`,
        });
      }
    }

    // 3. Receiver Address Validation (Either City+Address or National Address)
    if (!nationalAddress && (!receiverCity || !receiverAddress)) {
      errors.push({
        row: rowNumber,
        field: "receiver_address",
        message: "Address details missing. Provide either Receiver City + Address or National Address.",
      });
    }

    // 4. Payment Type Validation
    if (!paymentType) {
      errors.push({ row: rowNumber, field: "payment_type", message: "Payment Type (COD or CC) is required." });
    } else if (paymentType !== "COD" && paymentType !== "CC") {
      errors.push({
        row: rowNumber,
        field: "payment_type",
        message: `Invalid Payment Type '${paymentType}'. Must be 'COD' or 'CC'.`,
      });
    }

    // 5. COD Amount Validation
    const codVal = parseFloat(codAmountStr);
    if (paymentType === "COD") {
      if (isNaN(codVal) || codVal <= 0) {
        errors.push({
          row: rowNumber,
          field: "cod_amount",
          message: `COD Amount '${codAmountStr}' must be a valid number > 0 for COD payment.`,
        });
      }
    } else if (paymentType === "CC") {
      if (!isNaN(codVal) && codVal !== 0) {
        errors.push({
          row: rowNumber,
          field: "cod_amount",
          message: `COD Amount must be 0 for Credit Card (CC) payment. Found '${codAmountStr}'.`,
        });
      }
    }

    // 6. Number of Pieces Validation
    const piecesVal = parseInt(piecesStr, 10);
    if (isNaN(piecesVal) || piecesVal < 1) {
      errors.push({
        row: rowNumber,
        field: "no_of_pieces",
        message: `No of Pieces '${piecesStr}' must be a valid integer ≥ 1.`,
      });
    }

    // --- Order-Type Specific Validation ---

    if (orderType === "LM") {
      // LM Order specific rules
      const weightVal = parseFloat(weightStr);
      if (isNaN(weightVal) || weightVal <= 0) {
        errors.push({
          row: rowNumber,
          field: "weight",
          message: `Weight '${weightStr}' must be a valid number > 0 for LM orders.`,
        });
      }

      // Check duplicate Reference No in LM (In LM, each row is a unique order)
      if (refNo) {
        if (seenLmRefNumbers.has(refNo)) {
          const firstRow = seenLmRefNumbers.get(refNo);
          errors.push({
            row: rowNumber,
            field: "reference_no",
            message: `Duplicate Reference No '${refNo}' already used on Row ${firstRow}. Each LM order row must have a unique Reference No.`,
          });
        } else {
          seenLmRefNumbers.set(refNo, rowNumber);
        }
      }
    } else if (orderType === "FM") {
      // FM Order specific rules (Product SKU & Quantity required for every row)
      if (!productId) {
        errors.push({
          row: rowNumber,
          field: "product_id",
          message: "Product ID (SKU) is required for FM order items.",
        });
      }

      const qtyVal = parseInt(quantityStr, 10);
      if (!quantityStr || isNaN(qtyVal) || qtyVal < 1) {
        errors.push({
          row: rowNumber,
          field: "quantity",
          message: `Product Quantity '${quantityStr}' must be a valid integer ≥ 1.`,
        });
      }

      // Check multi-product order consistency across rows with same client_id + reference_no
      if (refNo && clientId) {
        const orderKey = `${clientId}::${refNo}`;
        const existingGroup = fmOrderGroups.get(orderKey);

        if (!existingGroup) {
          const skuMap = new Map<string, number>();
          if (productId) skuMap.set(productId.toLowerCase(), rowNumber);

          fmOrderGroups.set(orderKey, {
            firstRow: rowNumber,
            receiverName,
            receiverMobile,
            paymentType,
            codAmount: codAmountStr,
            seenSkus: skuMap,
          });
        } else {
          // Check receiver & order metadata consistency across product rows
          if (
            receiverName.toLowerCase() !== existingGroup.receiverName.toLowerCase() ||
            paymentType !== existingGroup.paymentType
          ) {
            errors.push({
              row: rowNumber,
              field: "reference_no",
              message: `Order details (Receiver / Payment) for Reference '${refNo}' do not match Row ${existingGroup.firstRow}. Multi-product rows for the same order must have identical Receiver details.`,
            });
          }

          // Check duplicate SKU under the same order reference
          if (productId) {
            const lowerSku = productId.toLowerCase();
            if (existingGroup.seenSkus.has(lowerSku)) {
              const prevSkuRow = existingGroup.seenSkus.get(lowerSku);
              errors.push({
                row: rowNumber,
                field: "product_id",
                message: `Duplicate Product SKU '${productId}' for Reference '${refNo}' already listed on Row ${prevSkuRow}.`,
              });
            } else {
              existingGroup.seenSkus.set(lowerSku, rowNumber);
            }
          }
        }
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    dataRowCount: dataRowsParsed.length,
  };
};
